"use client";

import { motion } from "framer-motion";
import Link from "next/link";
import {
	IconBroadcast,
	IconUsersGroup,
	IconArrowRight,
	IconHeart,
	type Icon,
} from "@/lib/tabler-icons";
import { SectionHeading } from "@/components/ui/editorial/SectionHeading";
import { Kicker } from "@/components/ui/editorial/Kicker";
import { activismAreas } from "../data/activism";

// Resuelve el icono de cada ámbito a partir de su nombre en los datos.
const iconMap: Record<string, Icon> = {
	IconBroadcast,
	IconUsersGroup,
};

const ActivismSection = () => {
	return (
		<section className="p-4 xl:px-0 py-16" aria-label="Activismo">
			<div className="container max-w-7xl mx-auto space-y-12">
				{/* Header */}
				<motion.header
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6 }}
					viewport={{ once: true }}
					className="space-y-4"
				>
					<SectionHeading
						index="04"
						title={
							<>
								<em className="italic text-terracotta">Activismo</em>
							</>
						}
						as="h2"
					/>
					<p className="font-body text-muted-foreground text-lg max-w-2xl leading-relaxed">
						Compromiso social desde la palabra y el debate. Participación activa
						en medios de comunicación y mesas redondas para visibilizar
						realidades, generar conciencia e impulsar el cambio.
					</p>
				</motion.header>

				{/* Activism Areas */}
				<div className="grid grid-cols-1 md:grid-cols-2 gap-8">
					{activismAreas.map((area, index) => {
						const AreaIcon = iconMap[area.icon];

						return (
							<motion.article
								key={area.id}
								initial={{ opacity: 0, y: 30 }}
								whileInView={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.6, delay: index * 0.1 }}
								viewport={{ once: true }}
								className="border border-border rounded-xl p-8 space-y-6 hover:border-terracotta transition-colors duration-200"
							>
								<div className="space-y-3">
									<div className="inline-flex items-center justify-center w-12 h-12 border border-border rounded-xl">
										{AreaIcon && (
											<AreaIcon className="w-6 h-6 text-terracotta" />
										)}
									</div>
									<Kicker>{area.subtitle}</Kicker>
									<h3 className="font-display font-semibold text-ink text-2xl md:text-3xl leading-tight">
										{area.title}
									</h3>
									<p className="font-body text-ink text-base leading-relaxed">
										{area.description}
									</p>
								</div>

								<ul className="space-y-2">
									{area.highlights.map((highlight, highlightIndex) => (
										<li
											key={highlightIndex}
											className="flex items-start gap-3"
										>
											<span className="text-terracotta mt-1.5 shrink-0 text-xs">
												—
											</span>
											<span className="font-body text-ink text-base">
												{highlight}
											</span>
										</li>
									))}
								</ul>
							</motion.article>
						);
					})}
				</div>

				{/* Call to Action */}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6 }}
					viewport={{ once: true }}
					className="mt-20 border-t border-border pt-12 space-y-6"
				>
					<div className="space-y-2">
						<Kicker>¿Quieres contar con mi participación?</Kicker>
						<h3 className="font-display font-semibold text-ink text-2xl md:text-3xl">
							Invítame a tu medio o mesa redonda
						</h3>
						<p className="font-body text-muted-foreground text-lg max-w-2xl leading-relaxed">
							Estoy disponible para entrevistas, coloquios y mesas redondas
							sobre igualdad, infancia, vínculos saludables y transformación
							social. Hablemos.
						</p>
					</div>
					<div className="flex flex-col sm:flex-row gap-4">
						<Link
							href="/asociacion-equilibrio#contacto"
							className="inline-flex items-center justify-center gap-2 font-display text-sm bg-ink text-cream px-6 py-3 rounded-lg hover:bg-ink/90 transition-colors duration-200"
						>
							<IconHeart className="w-4 h-4" />
							Solicitar mi participación
							<IconArrowRight className="w-4 h-4" />
						</Link>
					</div>
				</motion.div>
			</div>
		</section>
	);
};

export default ActivismSection;
